import React from 'react';
import { BookModel } from '../../../../models';
import { Trash2, Layers, Clock, Paperclip, ListOrdered, Video, Files, GraduationCap, BookOpen } from 'lucide-react';

interface BookCardProps {
  book: BookModel;
  onDelete: (book: BookModel) => void;
  onEdit: (book: BookModel) => void;
  isSelected?: boolean;
  currentUserId?: string;
  currentUserRole?: string;
}

const BookCard: React.FC<BookCardProps> = ({ book, onDelete, onEdit, isSelected, currentUserId, currentUserRole }) => {
  const getSubjectColor = (subject: string) => {
    const colors: Record<string, string> = {
      '국어': 'bg-rose-500',
      '수학': 'bg-blue-600',
      '영어': 'bg-indigo-600',
      '한국사': 'bg-amber-700',
      '사회': 'bg-orange-600',
      '과학': 'bg-emerald-600',
    };
    return colors[subject] || 'bg-slate-600';
  };

  return (
    <div
      onClick={() => onEdit(book)}
      className={`group flex flex-row p-3 items-stretch gap-3 bg-white hover:bg-slate-50/50 transition-all border rounded-2xl cursor-pointer h-[88px] ${
        isSelected
          ? 'border-indigo-500 ring-2 ring-indigo-500/20 shadow-md bg-indigo-50/10'
          : 'border-slate-200 hover:border-indigo-300 hover:shadow-md shadow-sm'
      }`}
    >
      {/* Left: Cover Image Thumbnail (Leading position) */}
      <div className="w-12 h-full flex-shrink-0 rounded-lg overflow-hidden shadow-sm border border-slate-100 bg-slate-50 flex items-center justify-center relative select-none">
        {book.coverUrl ? (
          <img 
            src={book.coverUrl} 
            alt={book.title} 
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
          />
        ) : (
          <div className={`w-full h-full ${getSubjectColor(book.subject)} flex flex-col items-center justify-center text-white/90 gap-1`}>
            <BookOpen size={16} strokeWidth={2.5} />
            <span className="text-[8px] font-black">{book.subject || '교재'}</span>
          </div>
        )}
      </div>

      {/* Right: Info Content */}
      <div className="flex-1 flex flex-col justify-center min-w-0 relative">
        <div className="flex items-start justify-between gap-2">
          {/* Text fields */}
          <div className="flex-1 min-w-0">
            <span className="text-[9px] font-black text-indigo-600 uppercase tracking-wider block truncate">
              {[book.subject, book.grade, book.type].filter(Boolean).join(' | ')}
            </span>
            <h4 className="font-bold text-slate-800 text-[12px] leading-snug line-clamp-1 mt-0.5 group-hover:text-indigo-600 transition-colors" title={book.title}>
              {book.title}
            </h4>
            <span className="text-[9px] text-slate-400 font-bold mt-0.5 block truncate">
              {[book.publisher, book.year].filter(Boolean).join(' · ')}
            </span>
          </div>

          {/* Action tags */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[7px] font-bold text-slate-400 bg-slate-100 px-1 py-0.5 rounded border border-slate-200/50">
              {book.id.split('-')[0]}
            </span>
            {(currentUserRole === 'super_admin' || (book.createdBy && book.createdBy === currentUserId)) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(book);
                }}
                className="p-1 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-md transition-colors"
                title="삭제"
              >
                <Trash2 size={11} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookCard;
